// Pure connection-config type guards + §2.13 interface labelling (agent.md
// module plan, UI Layering Sweep Phase 3). Extracted from Agent.tsx so the
// unified transcripts surface (ConnectionCheckLauncher, transcript columns)
// imports the one labelling switch instead of duplicating it. No React, no
// network.
//
// §2.13 law: the customer-facing label names the conversation modality
// (Chat/Voice) — provider detail lives only inside form fields and stored
// config. Non-running transports (legacy websocket rows without the sealed
// direct provider, sip, unknown configs) are labelled "— not running" and are
// not editable.
import type { components } from "../../../api/schema";

type ConnectionConfig = components["schemas"]["ConnectionConfig"];
type ConnectionResponse = components["schemas"]["ConnectionResponse"];
type HttpConnectionConfig = components["schemas"]["HttpConnectionConfig"];
type LiveKitConnectionConfig = components["schemas"]["LiveKitConnectionConfig"];
type PipecatCloudWebrtcConnectionConfig =
  components["schemas"]["PipecatCloudWebrtcConnectionConfig"];
type PipecatCloudWebsocketConnectionConfig =
  components["schemas"]["PipecatCloudWebsocketConnectionConfig"];
type SipConnectionConfig = components["schemas"]["SipConnectionConfig"];
type TwilioConnectionConfig = components["schemas"]["TwilioConnectionConfig"];
type WebsocketConnectionConfig =
  components["schemas"]["WebsocketConnectionConfig"];

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function isHttpConfig(
  config: ConnectionConfig | null,
): config is HttpConnectionConfig {
  return (
    config !== null &&
    "url" in config &&
    typeof config.url === "string" &&
    config.url.startsWith("https://")
  );
}

export function isWebsocketConfig(
  config: ConnectionConfig | null,
): config is WebsocketConnectionConfig {
  return (
    config !== null &&
    "url" in config &&
    typeof config.url === "string" &&
    config.url.startsWith("wss://")
  );
}

export function isDirectWebsocketConfig(
  config: ConnectionConfig | null,
): config is WebsocketConnectionConfig {
  return isWebsocketConfig(config) && hasDirectProvider(config);
}

function hasDirectProvider(value: unknown): boolean {
  return isRecord(value) && value.provider === "direct";
}

export function isTwilioConfig(
  config: ConnectionConfig | null,
): config is TwilioConnectionConfig {
  return (
    config !== null && "provider" in config && config.provider === "twilio"
  );
}

export function isLiveKitConfig(
  config: ConnectionConfig | null,
): config is LiveKitConnectionConfig {
  return (
    config !== null && "provider" in config && config.provider === "livekit"
  );
}

// The two Pipecat Cloud configs share `provider: "pipecat_cloud"`; only the
// websocket (chat) shape carries `outbound_pattern`.
export function isPipecatCloudWebrtcConfig(
  config: ConnectionConfig | null,
): config is PipecatCloudWebrtcConnectionConfig {
  return (
    config !== null &&
    "provider" in config &&
    config.provider === "pipecat_cloud" &&
    !("outbound_pattern" in config)
  );
}

export function isPipecatCloudWebsocketConfig(
  config: ConnectionConfig | null,
): config is PipecatCloudWebsocketConnectionConfig {
  return (
    config !== null &&
    "provider" in config &&
    config.provider === "pipecat_cloud" &&
    "outbound_pattern" in config
  );
}

export function isSipConfig(
  config: ConnectionConfig | null,
): config is SipConnectionConfig {
  return config !== null && "provider" in config && config.provider === "sip";
}

export function isEditableConnection(connection: ConnectionResponse): boolean {
  return (
    (connection.transport === "http" && isHttpConfig(connection.config)) ||
    (connection.transport === "telephony" &&
      isTwilioConfig(connection.config)) ||
    (connection.transport === "webrtc" && isLiveKitConfig(connection.config)) ||
    (connection.transport === "webrtc" &&
      isPipecatCloudWebrtcConfig(connection.config)) ||
    (connection.transport === "websocket" &&
      isDirectWebsocketConfig(connection.config)) ||
    (connection.transport === "websocket" &&
      isPipecatCloudWebsocketConfig(connection.config))
  );
}

export function isRunningInterface(connection: ConnectionResponse): boolean {
  return isEditableConnection(connection);
}

// §2.13 label set (post-merge 2026-07-15). The Pipecat Cloud branches close
// the flagged upstream gap where webrtc/pipecat_cloud (voice) and
// websocket/pipecat_cloud (chat) rows fell through to "WebSocket — not
// running" while isRunningInterface badged them success.
export function interfaceLabel(connection: ConnectionResponse): string {
  if (connection.transport === "http") {
    return "Chat (HTTP)";
  }
  if (
    connection.transport === "telephony" &&
    isTwilioConfig(connection.config)
  ) {
    return connection.initiator === "agent"
      ? "Voice — outbound agent"
      : "Voice — inbound agent";
  }
  if (connection.transport === "telephony") {
    return "Voice — not running";
  }
  if (connection.transport === "webrtc" && isLiveKitConfig(connection.config)) {
    return connection.config.audio !== null
      ? "Voice (LiveKit WebRTC)"
      : "Chat (LiveKit data)";
  }
  if (
    connection.transport === "webrtc" &&
    isPipecatCloudWebrtcConfig(connection.config)
  ) {
    return "Voice (Pipecat Cloud Daily)";
  }
  if (
    connection.transport === "websocket" &&
    isDirectWebsocketConfig(connection.config)
  ) {
    return connection.config.audio !== null
      ? "Voice (direct WebSocket)"
      : "Chat (direct WebSocket)";
  }
  if (
    connection.transport === "websocket" &&
    isPipecatCloudWebsocketConfig(connection.config)
  ) {
    return "Chat (Pipecat Cloud WebSocket)";
  }
  return "WebSocket — not running";
}

// The row's Target column: url / from-or-to number by initiator / sip_uri /
// LiveKit server_url / "-" (Pipecat Cloud configs carry no address).
export function targetLabel(connection: ConnectionResponse): string {
  const config = connection.config;
  if (isHttpConfig(config) || isWebsocketConfig(config)) {
    return config.url;
  }
  if (isTwilioConfig(config)) {
    return connection.initiator === "agent"
      ? config.from_number
      : config.to_number;
  }
  if (isSipConfig(config)) {
    return config.sip_uri;
  }
  if (isLiveKitConfig(config)) {
    return config.server_url;
  }
  return "-";
}
